<script setup>
import { computed } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import {
    PhBookOpen, PhCurrencyDollar, PhUser, PhArrowRight,
    PhCheckCircle, PhWarningCircle, PhCalendar, PhTrophy,
    PhChartBar, PhClockCountdown,
} from '@phosphor-icons/vue'
import Layout  from '@pages/Shared/layouts/StudentLayout.vue'

const props = defineProps({
    student:        Object,
    activeSession:  Object,
    activeTerm:     Object,
    classRoom:      Object,
    recentResults:  Array,   // last term results summary
    feesSummary:    Object,  // { total, paid, balance, status }
    announcements:  Array,   // optional notices
})

const greeting = computed(() => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
})

const feeProgress = computed(() => {
    if (!props.feesSummary?.total) return 0
    return Math.min(100, Math.round((props.feesSummary.paid / props.feesSummary.total) * 100))
})

const feeStatusColor = computed(() => ({
    paid:         'text-green-600',
    partial:      'text-yellow-600',
    unpaid:       'text-red-500',
}[props.feesSummary?.status] ?? 'text-gray-500'))

const feeBarColor = computed(() => ({
    paid:    'bg-green-500',
    partial: 'bg-yellow-400',
    unpaid:  'bg-red-400',
}[props.feesSummary?.status] ?? 'bg-gray-300'))
</script>

<template>
    <Layout>
        <Head title="Dashboard" />

        <!-- Greeting -->
        <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">
                {{ greeting }}, {{ student.first_name }}
            </h1>
            <p class="text-gray-500 text-sm mt-1">
                {{ activeSession?.name }} — {{ activeTerm?.name }}
                · <span class="font-medium text-gray-700">{{ classRoom?.name }}</span>
            </p>
        </div>

        <!-- Stats row -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">

            <div class="bg-white rounded-lg border border-gray-200 p-5">
                <div class="flex items-center justify-between mb-3">
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Class</p>
                    <div class="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <PhBookOpen :size="16" class="text-primary" />
                    </div>
                </div>
                <p class="text-lg font-bold text-gray-900">{{ classRoom?.name ?? '—' }}</p>
                <p class="text-xs text-gray-400 mt-0.5">{{ classRoom?.section?.name }}</p>
            </div>

            <div class="bg-white rounded-lg border border-gray-200 p-5">
                <div class="flex items-center justify-between mb-3">
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Student ID</p>
                    <div class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <PhUser :size="16" class="text-indigo-600" />
                    </div>
                </div>
                <p class="text-base font-bold text-gray-900 font-mono">{{ student.id }}</p>
                <p class="text-xs text-gray-400 mt-0.5">Reg. Number</p>
            </div>

            <div class="bg-white rounded-lg border border-gray-200 p-5">
                <div class="flex items-center justify-between mb-3">
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Last Result</p>
                    <div class="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                        <PhTrophy :size="16" class="text-purple-600" />
                    </div>
                </div>
                <p class="text-lg font-bold text-gray-900">
                    {{ recentResults?.[0]?.average ?? '—' }}
                    <span v-if="recentResults?.[0]?.average" class="text-sm font-medium text-gray-400">%</span>
                </p>
                <p class="text-xs text-gray-400 mt-0.5">Average score</p>
            </div>

            <div class="bg-white rounded-lg border border-gray-200 p-5">
                <div class="flex items-center justify-between mb-3">
                    <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide">Fee Balance</p>
                    <div class="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                        <PhCurrencyDollar :size="16" class="text-amber-600" />
                    </div>
                </div>
                <p class="text-lg font-bold" :class="feeStatusColor">
                    ₦{{ Number(feesSummary?.balance ?? 0).toLocaleString() }}
                </p>
                <p class="text-xs text-gray-400 mt-0.5 capitalize">{{ feesSummary?.status ?? '—' }}</p>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <!-- Left col: Fees + Quick Links -->
            <div class="lg:col-span-1 space-y-5">

                <!-- School Fees Card -->
                <div class="bg-white rounded-lg border border-gray-200 p-5">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="font-semibold text-gray-800 text-sm">School Fees</h3>
                        <span class="text-xs px-2 py-0.5 rounded-full font-semibold capitalize"
                            :class="{
                                'bg-green-50 text-green-700':  feesSummary?.status === 'paid',
                                'bg-yellow-50 text-yellow-700': feesSummary?.status === 'partial',
                                'bg-red-50 text-red-600':      feesSummary?.status === 'unpaid',
                            }">
                            {{ feesSummary?.status ?? '—' }}
                        </span>
                    </div>

                    <div class="space-y-2 text-sm mb-4">
                        <div class="flex justify-between">
                            <span class="text-gray-400">Total Fees</span>
                            <span class="font-semibold text-gray-800">
                                ₦{{ Number(feesSummary?.total ?? 0).toLocaleString() }}
                            </span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-gray-400">Amount Paid</span>
                            <span class="font-semibold text-green-600">
                                ₦{{ Number(feesSummary?.paid ?? 0).toLocaleString() }}
                            </span>
                        </div>
                        <div class="flex justify-between">
                            <span class="text-gray-400">Balance</span>
                            <span class="font-semibold" :class="feeStatusColor">
                                ₦{{ Number(feesSummary?.balance ?? 0).toLocaleString() }}
                            </span>
                        </div>
                    </div>

                    <!-- Progress bar -->
                    <div class="w-full bg-gray-100 rounded-full h-2 mb-1">
                        <div class="h-2 rounded-full transition-all duration-700"
                            :class="feeBarColor"
                            :style="`width: ${feeProgress}%`" />
                    </div>
                    <p class="text-xs text-gray-400 text-right">{{ feeProgress }}% paid</p>

                    <Link href="/student/payments"
                        class="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg
                               bg-primary hover:bg-primary-light text-white text-sm font-semibold transition">
                        <PhCurrencyDollar :size="15" />
                        Make Payment
                    </Link>
                </div>

                <!-- Quick links -->
                <div class="bg-white rounded-lg border border-gray-200 p-5">
                    <h3 class="font-semibold text-gray-800 text-sm mb-4">Quick Links</h3>
                    <div class="space-y-1.5">
                        <Link v-for="item in [
                            { label: 'View Results',      href: '/student/results',  icon: PhBookOpen       },
                            { label: 'Make Payment',      href: '/student/payments', icon: PhCurrencyDollar },
                            { label: 'My Profile',        href: '/student/profile',  icon: PhUser           },
                        ]" :key="item.label" :href="item.href"
                            class="flex items-center justify-between px-4 py-2.5 rounded-lg
                                   bg-gray-50 hover:bg-primary/5 border border-transparent
                                   hover:border-primary/10 transition group">
                            <div class="flex items-center gap-3">
                                <component :is="item.icon" :size="16" class="text-primary" />
                                <span class="text-sm font-medium text-gray-700">{{ item.label }}</span>
                            </div>
                            <PhArrowRight :size="14" class="text-gray-400 group-hover:text-primary transition" />
                        </Link>
                    </div>
                </div>
            </div>

            <!-- Right col: Recent results + Announcements -->
            <div class="lg:col-span-2 space-y-5">

                <!-- Recent Results -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                        <div class="flex items-center gap-2">
                            <PhChartBar :size="16" class="text-primary" />
                            <h3 class="font-semibold text-gray-800 text-sm">Recent Results</h3>
                        </div>
                        <Link href="/student/results"
                            class="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                            View All <PhArrowRight :size="12" />
                        </Link>
                    </div>

                    <div v-if="recentResults?.length" class="divide-y divide-gray-50">
                        <div v-for="result in recentResults.slice(0, 5)" :key="result.subject_id"
                            class="px-5 py-3.5 flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-800">{{ result.subject_name }}</p>
                                <p class="text-xs text-gray-400 mt-0.5">{{ result.term_name }}</p>
                            </div>
                            <div class="flex items-center gap-3">
                                <!-- Score bar mini -->
                                <div class="hidden sm:flex items-center gap-2">
                                    <div class="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div class="h-full rounded-full"
                                            :class="{
                                                'bg-green-500':  result.total >= 70,
                                                'bg-yellow-400': result.total >= 50 && result.total < 70,
                                                'bg-red-400':    result.total < 50,
                                            }"
                                            :style="`width: ${result.total}%`" />
                                    </div>
                                </div>
                                <span class="text-sm font-bold w-10 text-right"
                                    :class="{
                                        'text-green-600':  result.total >= 70,
                                        'text-yellow-600': result.total >= 50 && result.total < 70,
                                        'text-red-500':    result.total < 50,
                                    }">
                                    {{ result.total }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-5 py-10 text-center text-sm text-gray-400">
                        No results published yet for this term.
                    </div>
                </div>

                <!-- Announcements / Notices -->
                <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                        <PhClockCountdown :size="16" class="text-primary" />
                        <h3 class="font-semibold text-gray-800 text-sm">Notices</h3>
                    </div>
                    <div v-if="announcements?.length" class="divide-y divide-gray-50">
                        <div v-for="notice in announcements" :key="notice.id"
                            class="px-5 py-4">
                            <div class="flex items-start gap-3">
                                <div class="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                                <div>
                                    <p class="text-sm font-medium text-gray-800">{{ notice.title }}</p>
                                    <p class="text-xs text-gray-500 mt-0.5">{{ notice.body }}</p>
                                    <p class="text-xs text-gray-400 mt-1">{{ notice.date }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-5 py-8 text-center text-sm text-gray-400">
                        No notices at this time.
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>